import { type ReactNode, type FC } from "react";
import { useFormContext } from "react-hook-form";
import { RadioButton } from "@root/shared/components/RadioButton";
import { TextArea } from "@root/shared/components/TextArea";
import { InformationText } from "@root/shared/components/InformationText";
import { Heading } from "@root/shared/components/Heading";
import { Label } from "@root/shared/components/Label";
import * as styles from "./styles.css";

type Option = {
  optionCode: string;
  label: string;
  requiresFollowUpText: boolean;
};

type FormField = {
  name: string;
  followUpTextName: string;
};

type Props = {
  question: ReactNode;
  options: Option[];
  formField: FormField;
};

const PLACEHOLDER = "「その他」の場合は、具体的な内容を必ずご入力ください。";

export const SingleChoiceQuestion: FC<Props> = ({ question, options, formField }) => {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext();
  const radioError = errors[formField.name];
  const followUpTextError = errors[formField.followUpTextName];

  const watchedValue = watch(formField.name);
  const selectedOptionCode = typeof watchedValue === "string" ? watchedValue : "";
  const requiresFollowUpText = options.some(
    (option) => option.requiresFollowUpText && option.optionCode === selectedOptionCode,
  );

  return (
    <>
      <Heading tag="p" size="medium" bold className={styles.heading}>
        {question}
        <Label theme="red" className={styles.headingLabel}>
          必須
        </Label>
      </Heading>
      {radioError && (
        <div className={styles.errorText}>
          <InformationText text={radioError.message} isAlert />
        </div>
      )}
      <ul className={styles.list}>
        {options.map((option) => {
          const id = `radio-${formField.name}-${option.optionCode}`;
          return (
            <li key={option.optionCode} className={styles.listItem}>
              <RadioButton
                id={id}
                value={option.optionCode}
                label={option.label}
                {...register(formField.name)}
                hasError={!!radioError}
                className={styles.radio}
              />
            </li>
          );
        })}
      </ul>
      {requiresFollowUpText && (
        <div className={styles.otherTextArea}>
          {followUpTextError && (
            <div className={styles.errorText}>
              <InformationText text={followUpTextError.message} isAlert />
            </div>
          )}
          <TextArea
            {...register(formField.followUpTextName)}
            placeholder={PLACEHOLDER}
            hasError={!!followUpTextError}
          />
        </div>
      )}
    </>
  );
};
